/**
 * DueLessonsScreen — "Due for review" (LESSON-006).
 *
 * The spaced-repetition queue: every lesson whose latest attempt has come
 * due, split into Overdue and Ready for review. Consumes
 * GET /api/lessons/scheduled via the lessonStore due slice.
 *
 * Unlike the Practice hub card, this screen reports fetch failure — it is
 * the surface the learner explicitly asked for (DUE-007).
 *
 * Spec: docs/specs/mobile/lessons.mobile.md § LESSON-006
 */

import React, { useCallback, useMemo } from 'react';
import { ActivityIndicator, SectionList, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import { ErrorState } from '../components/ui';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { useLessonStore, type ScheduledLessonItem } from '../store/lessonStore';
import {
  selectDueLessons,
  selectDueLoading,
  selectDueError,
  selectLoadDueLessons,
} from '../store/lessonSelectors';

interface DueSection {
  key: 'overdue' | 'ready';
  title: string;
  data: ScheduledLessonItem[];
}

// ── Row ───────────────────────────────────────────────────────────

const DueLessonRow = React.memo(function DueLessonRow({
  item,
  onPress,
}: {
  item: ScheduledLessonItem;
  onPress: (slug: string, title: string) => void;
}) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const { lesson, overdueDays, lastScore } = item;

  // Server-computed overdueDays — never recomputed from dueDate, so the
  // label can't drift with device clock skew (DUE-004).
  const dueLabel =
    overdueDays === 0
      ? t('lessons.due.dueToday')
      : t('lessons.due.overdueBy', { count: overdueDays });

  const handlePress = useCallback(
    () => onPress(lesson.slug, lesson.title),
    [onPress, lesson.slug, lesson.title],
  );

  return (
    <TouchableOpacity
      testID={`due-lesson-${lesson.slug}`}
      accessibilityRole="button"
      accessibilityLabel={`${lesson.title}, ${dueLabel}`}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={[styles.row, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <View style={styles.rowBody}>
          <Text
            allowFontScaling={false}
            numberOfLines={2}
            style={{ fontSize: 16 * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
          >
            {lesson.title}
          </Text>
          <Text
            allowFontScaling={false}
            numberOfLines={1}
            style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
          >
            {dueLabel} · {t('lessons.due.lastScore', { score: lastScore })}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
});

// ── Screen ────────────────────────────────────────────────────────

export function DueLessonsScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation: any = useNavigation();

  const dueLessons = useLessonStore(selectDueLessons);
  const dueLoading = useLessonStore(selectDueLoading);
  const dueError = useLessonStore(selectDueError);
  const loadDueLessons = useLessonStore(selectLoadDueLessons);

  useFocusEffect(
    useCallback(() => {
      void loadDueLessons();
    }, [loadDueLessons]),
  );

  const handleBack = useCallback(() => navigation.goBack(), [navigation]);
  const handleRetry = useCallback(() => void loadDueLessons(), [loadDueLessons]);

  const handleLessonPress = useCallback(
    (slug: string, title: string) => {
      navigation?.push?.('LessonDetail', { slug, title });
    },
    [navigation],
  );

  // Empty groups are dropped entirely rather than rendering a bare
  // header (DUE-005).
  const sections = useMemo<DueSection[]>(() => {
    const overdue = dueLessons.filter((l) => l.overdueDays >= 1);
    const ready = dueLessons.filter((l) => l.overdueDays === 0);
    const out: DueSection[] = [];
    if (overdue.length > 0) {
      out.push({ key: 'overdue', title: t('lessons.due.sectionOverdue'), data: overdue });
    }
    if (ready.length > 0) {
      out.push({ key: 'ready', title: t('lessons.due.sectionReady'), data: ready });
    }
    return out;
  }, [dueLessons, t]);

  const renderItem = useCallback(
    ({ item }: { item: ScheduledLessonItem }) => (
      <DueLessonRow item={item} onPress={handleLessonPress} />
    ),
    [handleLessonPress],
  );

  const renderSectionHeader = useCallback(
    ({ section }: { section: DueSection }) => (
      <Text
        allowFontScaling={false}
        accessibilityRole="header"
        style={{
          fontSize: 18 * fontScale,
          fontWeight: '700',
          color: theme.text,
          marginTop: 8,
          marginBottom: 8,
          ...ffs,
        }}
      >
        {section.title}
      </Text>
    ),
    [theme.text, fontScale, ffs],
  );

  const keyExtractor = useCallback((item: ScheduledLessonItem) => item.lesson.id, []);

  const body = () => {
    if (dueError && dueLessons.length === 0) {
      return (
        <ErrorState
          testID="due-lessons-error"
          message={dueError}
          retryLabel={t('common.retry')}
          onRetry={handleRetry}
        />
      );
    }

    if (dueLoading && dueLessons.length === 0) {
      return <ActivityIndicator size="small" color={theme.primary} style={{ paddingVertical: 24 }} />;
    }

    if (dueLessons.length === 0) {
      return (
        <View testID="due-lessons-empty" style={styles.empty}>
          <Text
            allowFontScaling={false}
            accessible={false}
            importantForAccessibility="no"
            style={{ fontSize: 48 }}
          >
            🎉
          </Text>
          <Text
            allowFontScaling={false}
            style={{
              fontSize: 16 * fontScale,
              fontWeight: '600',
              color: theme.text,
              marginTop: 12,
              textAlign: 'center',
              ...ffs,
            }}
          >
            {t('lessons.due.empty')}
          </Text>
          <TouchableOpacity
            testID="due-lessons-browse"
            accessibilityRole="button"
            accessibilityLabel={t('lessons.due.browse')}
            onPress={handleBack}
            activeOpacity={0.7}
            style={[styles.browseButton, { borderColor: theme.border }]}
          >
            <Text
              allowFontScaling={false}
              style={{ fontSize: 14 * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
            >
              {t('lessons.due.browse')}
            </Text>
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <SectionList
        testID="due-lessons-list"
        sections={sections}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        stickySectionHeadersEnabled={false}
        contentContainerStyle={{ gap: 8, paddingBottom: 24 }}
        accessibilityLabel={t('lessons.due.title')}
      />
    );
  };

  return (
    <View style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title={t('lessons.due.title')} onBack={handleBack} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        <View testID="due-lessons-screen" style={styles.content}>
          {body()}
        </View>
      </WebContainer>
    </View>
  );
}

const styles = StyleSheet.create({
  content: { flex: 1, padding: 16 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
  },
  rowBody: { flex: 1 },
  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  browseButton: {
    marginTop: 16,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
  },
});
